/**
 * Browser Cognito auth integration for the Owner admin (Req 3.1–3.5).
 *
 * Wraps `amazon-cognito-identity-js` so the rest of the admin UI deals in
 * simple promises and a small set of outcome shapes rather than the SDK's
 * callback style. All session storage (the ID/access/refresh tokens) is handled
 * by the SDK against `localStorage`, so a page reload can transparently restore
 * the session and silently refresh an expired ID token using the refresh token.
 *
 * Configuration comes from Vite build-time env vars:
 *   - `VITE_COGNITO_USER_POOL_ID` — the user pool id (CfnOutput `UserPoolId`)
 *   - `VITE_COGNITO_CLIENT_ID`    — the app client id (CfnOutput `UserPoolClientId`)
 *
 * Both must be set at build time; if either is missing we fail loudly with a
 * clear message rather than producing confusing SDK errors at sign-in time.
 *
 * The token surfaced to the API client is the Cognito **ID token**, which
 * carries the `aud` claim the API Gateway Cognito JWT authorizer validates
 * against the app client id.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

/** Reads a required Vite env var or throws a clear configuration error. */
function requireEnv(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `Missing required configuration "${String(name)}". Set it at build time ` +
        `(e.g. in packages/frontend/.env) from the CDK stack outputs. See the ` +
        `frontend README for details.`,
    );
  }
  return value.trim();
}

let cachedPool: CognitoUserPool | undefined;

/**
 * Returns the configured user pool, constructing it lazily so a missing env
 * var surfaces only when auth is actually attempted (and with a clear error).
 */
export function getUserPool(): CognitoUserPool {
  if (cachedPool === undefined) {
    cachedPool = new CognitoUserPool({
      UserPoolId: requireEnv("VITE_COGNITO_USER_POOL_ID"),
      ClientId: requireEnv("VITE_COGNITO_CLIENT_ID"),
    });
  }
  return cachedPool;
}

/** A friendly, surfaced auth error with a stable machine code. */
export interface AuthError {
  code: string;
  message: string;
}

/** Outcome of a sign-in (or a completed new-password challenge). */
export type SignInResult =
  | { kind: "success"; idToken: string }
  | {
      kind: "newPasswordRequired";
      /** Completes first-login by setting a permanent password. */
      complete: (newPassword: string) => Promise<SignInResult>;
    }
  | { kind: "error"; error: AuthError };

/** Maps raw Cognito SDK errors to friendly, owner-facing messages. */
export function friendlyAuthError(err: unknown): AuthError {
  const code =
    (typeof err === "object" && err !== null && "code" in err
      ? String((err as { code?: unknown }).code)
      : undefined) ?? "AuthError";

  switch (code) {
    case "NotAuthorizedException":
      return { code, message: "Incorrect email or password. Please try again." };
    case "UserNotFoundException":
      return { code, message: "Incorrect email or password. Please try again." };
    case "UserNotConfirmedException":
      return {
        code,
        message: "This account isn't confirmed yet. Contact your administrator.",
      };
    case "PasswordResetRequiredException":
      return {
        code,
        message: "A password reset is required. Use \u201CForgot password?\u201D to set a new one.",
      };
    case "CodeMismatchException":
      return { code, message: "That code isn't correct. Check the email and try again." };
    case "ExpiredCodeException":
      return {
        code,
        message: "That code has expired. Request a new one and try again.",
      };
    case "InvalidPasswordException":
      return {
        code,
        message:
          "That password doesn't meet the requirements (at least 12 characters with " +
          "upper- and lower-case letters, a number, and a symbol).",
      };
    case "LimitExceededException":
      return {
        code,
        message: "Too many attempts. Please wait a little while and try again.",
      };
    case "InvalidParameterException":
      return { code, message: "Please check the information you entered and try again." };
    default: {
      const message =
        typeof err === "object" && err !== null && "message" in err
          ? String((err as { message?: unknown }).message)
          : "Something went wrong signing in. Please try again.";
      return { code, message };
    }
  }
}

/** Extracts the ID-token JWT from a Cognito session. */
function idTokenOf(session: CognitoUserSession): string {
  return session.getIdToken().getJwtToken();
}

/**
 * Signs in with email + password using SRP (`authenticateUser`).
 *
 * Resolves to:
 *   - `success` with the ID-token JWT on a normal sign-in,
 *   - `newPasswordRequired` (with a `complete` callback) when Cognito demands a
 *     first-login password change for an admin-created user,
 *   - `error` with a friendly message on bad credentials or other failures.
 *
 * Never rejects — every outcome is modelled in {@link SignInResult}.
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  const pool = getUserPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  const details = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise<SignInResult>((resolve) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        resolve({ kind: "success", idToken: idTokenOf(session) });
      },
      onFailure: (err) => {
        resolve({ kind: "error", error: friendlyAuthError(err) });
      },
      newPasswordRequired: () => {
        resolve({
          kind: "newPasswordRequired",
          complete: (newPassword: string) =>
            new Promise<SignInResult>((resolveComplete) => {
              // Pass no attribute overrides: the admin-created account already
              // has the required attributes (email); Cognito rejects immutable
              // ones here, so an empty object is the safe choice.
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: (session) =>
                    resolveComplete({
                      kind: "success",
                      idToken: idTokenOf(session),
                    }),
                  onFailure: (err) =>
                    resolveComplete({
                      kind: "error",
                      error: friendlyAuthError(err),
                    }),
                },
              );
            }),
        });
      },
    });
  });
}

/**
 * Starts the forgot-password flow: Cognito emails a verification code to the
 * account address. Resolves `{ ok: true }` once the code has been sent.
 */
export function forgotPassword(
  email: string,
): Promise<{ ok: true } | { ok: false; error: AuthError }> {
  const user = new CognitoUser({ Username: email, Pool: getUserPool() });
  return new Promise((resolve) => {
    user.forgotPassword({
      onSuccess: () => resolve({ ok: true }),
      onFailure: (err) => resolve({ ok: false, error: friendlyAuthError(err) }),
      // Called when a code has been delivered; treat the same as success so the
      // UI can advance to the "enter code + new password" step.
      inputVerificationCode: () => resolve({ ok: true }),
    });
  });
}

/**
 * Completes the forgot-password flow with the emailed code and a new password.
 */
export function confirmForgotPassword(
  email: string,
  verificationCode: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; error: AuthError }> {
  const user = new CognitoUser({ Username: email, Pool: getUserPool() });
  return new Promise((resolve) => {
    user.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => resolve({ ok: true }),
      onFailure: (err) => resolve({ ok: false, error: friendlyAuthError(err) }),
    });
  });
}

/**
 * Restores the persisted session (if any) and returns a current ID token,
 * silently refreshing it via the refresh token when the cached token has
 * expired. Returns `null` when there is no valid session (signed out or the
 * refresh token has also expired).
 */
export function getActiveIdToken(): Promise<string | null> {
  const user = getUserPool().getCurrentUser();
  if (user === null) {
    return Promise.resolve(null);
  }
  return new Promise<string | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err !== null || session === null || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(idTokenOf(session));
    });
  });
}

/** Signs the current owner out, clearing the persisted Cognito session. */
export function signOut(): void {
  const user = getUserPool().getCurrentUser();
  if (user !== null) {
    user.signOut();
  }
}

/**
 * Forces a token refresh using the stored refresh token, returning a freshly
 * minted ID token. Used to proactively rotate the ID token shortly before it
 * expires so management calls never carry a stale token. Returns `null` when
 * there is no session or the refresh token is no longer valid.
 */
export function refreshIdToken(): Promise<string | null> {
  const user = getUserPool().getCurrentUser();
  if (user === null) {
    return Promise.resolve(null);
  }
  return new Promise<string | null>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err !== null || session === null) {
        resolve(null);
        return;
      }
      user.refreshSession(
        session.getRefreshToken(),
        (refreshErr, newSession: CognitoUserSession | null) => {
          if (refreshErr || newSession === null) {
            resolve(null);
            return;
          }
          resolve(idTokenOf(newSession));
        },
      );
    });
  });
}
